import type { ReactElement } from "react"
import BaseContainer from "@gatsby-plugin-generic-page/components/BaseContainer"
import BaseSection from "@gatsby-plugin-generic-page/components/BaseSection"
import MarkdownText from "@gatsby-plugin-generic-page/components/Markdown/Text"
import Media from "@gatsby-plugin-generic-page/components/Media"
import type { CompanyInfoSectionFlatData } from "@gatsby-plugin-generic-page/fragments/companyInfoSection"
import { getBgColorClass } from "@gatsby-plugin-generic-page/fragments/udsBackgroundColor"
import { Card, HeroTile, Picture, Text } from "@ionos-web-design-system/react"
import { cn } from "~/helpers/cn"
import assetsSquidexToImageObject from "~/lib/assetsSquidexToImageObject"
import isEmpty from "~/lib/isEmpty"

export default function CompanyInfoSection({
  companyInfoBox,
  tiles,
  sectionSettings,
}: CompanyInfoSectionFlatData): ReactElement<CompanyInfoSectionFlatData> {
  const backgroundImageAsset = !isEmpty(companyInfoBox.backgroundImage)
    ? assetsSquidexToImageObject(
        companyInfoBox.backgroundImage,
        companyInfoBox.title ?? ""
      )
    : undefined

  const hasText =
    !isEmpty(companyInfoBox.title) || !isEmpty(companyInfoBox.subtitle)

  const boxBgColorClass = getBgColorClass(companyInfoBox.backgroundColor)

  const tileText = hasText ? (
    <>
      {!isEmpty(companyInfoBox.title) ? (
        <Text color="accent" variant="heading5xl" weight="black">
          {companyInfoBox.title}
        </Text>
      ) : null}

      {!isEmpty(companyInfoBox.subtitle) ? (
        <Text color="default" variant="heading5xl" weight="black">
          {companyInfoBox.subtitle}
        </Text>
      ) : null}

      {!isEmpty(companyInfoBox.text) ? (
        <div className="text-lg mt-4">
          <MarkdownText>{companyInfoBox.text}</MarkdownText>
        </div>
      ) : null}
    </>
  ) : null

  return (
    <BaseSection {...sectionSettings}>
      <BaseContainer {...sectionSettings?.containerSettings}>
        <div className="grid grid-cols-1 md:grid-cols-[360px_1fr] lg:grid-cols-[520px_1fr] gap-3 md:gap-x-3 lg:gap-x-5">
          {hasText ? (
            <HeroTile
              data-testid="company-info-box"
              text={tileText}
              backgroundColor={
                boxBgColorClass === undefined ? "subtle" : undefined
              }
              className={cn(
                "rounded-md",
                boxBgColorClass === "" ? "bg-transparent" : boxBgColorClass
              )}
              image={
                backgroundImageAsset ? (
                  <Picture srcData={backgroundImageAsset} />
                ) : undefined
              }
            />
          ) : !isEmpty(companyInfoBox.backgroundImage) ? (
            <div className="hidden lg:block">
              <Media media={companyInfoBox.backgroundImage} />
            </div>
          ) : null}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {tiles.map((tile, index) => {
              const icon = !isEmpty(tile.icon)
                ? assetsSquidexToImageObject(tile.icon, tile.title)
                : undefined

              const boxContent = (
                <>
                  <Text variant="heading2xl" weight="black">
                    {tile.title}
                  </Text>

                  <div className="mt-3">
                    <Text variant="bodyLg">{tile.description}</Text>
                  </div>
                </>
              )

              const iconContent = icon
                ? {
                    icon: true,
                    iconContent: <Picture srcData={icon} />,
                  }
                : undefined

              const bgColorClass = getBgColorClass(tile.backgroundColor)

              return (
                <Card
                  key={index}
                  visual={iconContent}
                  className={cn("rounded-md md:p-4 lg:p-5", bgColorClass)}
                >
                  {boxContent}
                </Card>
              )
            })}
          </div>
        </div>
      </BaseContainer>
    </BaseSection>
  )
}
